import {makeAutoObservable, runInAction} from 'mobx'
import config from 'src/config'
import {MainStore} from 'src/store/MainStore'
import Institution from 'src/entities/Institution'

// InstitutionStatusEnum.NotApproved on the backend; "approved" = anything else.
const NOT_APPROVED_STATUS_ID = 3

// react-quill emits '<p><br></p>' for a visually-empty editor; treat stripped-empty HTML as empty.
const htmlIsEmpty = (html: string): boolean =>
  !html ||
  html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, '')
    .trim() === ''

export type RecipientMode = 'single' | 'all'

export class EmailComposeStore {
  subject: string = ''

  htmlBody: string = ''

  recipientMode: RecipientMode = 'single'

  selectedInstitution?: Institution

  attachments: File[] = []

  isSending: boolean = false

  constructor(readonly owner: MainStore) {
    makeAutoObservable(this)
  }

  addAttachments = (files: File[]) => {
    // Drop 0-byte files: the backend silently skips them, which would otherwise confuse the user.
    this.attachments = [...this.attachments, ...files.filter(f => f.size > 0)]
  }

  removeAttachment = (index: number) => {
    this.attachments = this.attachments.filter((_, i) => i !== index)
  }

  // Mirror the backend recipient filter exactly (StatusId != NotApproved AND a non-empty contact email)
  // so the previewed count matches what the server actually sends.
  get approvedInstitutions(): Institution[] {
    return (this.owner.institutionStore.institutions ?? []).filter(
      i => i.statusId !== NOT_APPROVED_STATUS_ID && !!i.contact?.email?.trim()
    )
  }

  // Content-only validity — used by the institution's own Emails tab, where the recipient is fixed by
  // context and there is no institution to pick.
  get hasContent(): boolean {
    return !!this.subject.trim() && !htmlIsEmpty(this.htmlBody)
  }

  get canSend(): boolean {
    if (!this.subject.trim() || htmlIsEmpty(this.htmlBody)) return false
    if (this.recipientMode === 'single') return !!this.selectedInstitution
    return this.approvedInstitutions.length > 0
  }

  setSubject = (value: string) => {
    this.subject = value
  }

  setHtmlBody = (value: string) => {
    this.htmlBody = value
  }

  setRecipientMode = (mode: RecipientMode) => {
    this.recipientMode = mode
  }

  setSelectedInstitution = (institution?: Institution) => {
    this.selectedInstitution = institution
  }

  reset = () => {
    this.subject = ''
    this.htmlBody = ''
    this.selectedInstitution = undefined
    this.recipientMode = 'single'
    this.attachments = []
  }

  private buildFormData = (): FormData => {
    const fd = new FormData()
    fd.append('subject', this.subject)
    fd.append('htmlBody', this.htmlBody)
    this.attachments.forEach(f => fd.append('files', f, f.name))
    return fd
  }

  sendToSingle = async () => {
    const institution = this.selectedInstitution
    if (!institution) return
    runInAction(() => {
      this.isSending = true
    })
    try {
      const res = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Institutions/${institution.id}/SendCustomEmail`,
        {method: 'POST', body: this.buildFormData()}
      )
      if (res.status === 200) {
        this.owner.setMessage('המייל נשלח בהצלחה.', 'success')
        this.reset()
      } else {
        this.owner.setMessage('שליחת המייל נכשלה, נסה שוב.', 'error')
      }
    } catch (e) {
      this.owner.setMessage('שליחת המייל נכשלה, נסה שוב.', 'error')
    } finally {
      runInAction(() => {
        this.isSending = false
      })
    }
  }

  // Send to one institution by id — the institution's Emails tab, where the recipient comes from the
  // screen context instead of the picker. Same endpoint/payload as sendToSingle.
  sendToInstitution = async (institutionId?: number): Promise<boolean> => {
    if (!institutionId) return false
    runInAction(() => {
      this.isSending = true
    })
    try {
      const res = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Institutions/${institutionId}/SendCustomEmail`,
        {method: 'POST', body: this.buildFormData()}
      )
      if (res.status === 200) {
        this.owner.setMessage('המייל נשלח בהצלחה.', 'success')
        this.reset()
        return true
      }
      this.owner.setMessage('שליחת המייל נכשלה, נסה שוב.', 'error')
      return false
    } catch (e) {
      this.owner.setMessage('שליחת המייל נכשלה, נסה שוב.', 'error')
      return false
    } finally {
      runInAction(() => {
        this.isSending = false
      })
    }
  }

  sendToApproved = async () => {
    runInAction(() => {
      this.isSending = true
    })
    try {
      const res = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Institutions/SendCustomEmailToApproved`,
        {method: 'POST', body: this.buildFormData()}
      )
      if (res.status === 200) {
        const count = await res.json()
        this.owner.setMessage(
          `שליחת המייל ל-${count} מוסדות מאושרים החלה.`,
          'info'
        )
        this.reset()
      } else {
        this.owner.setMessage('שליחת המייל נכשלה, נסה שוב.', 'error')
      }
    } catch (e) {
      this.owner.setMessage('שליחת המייל נכשלה, נסה שוב.', 'error')
    } finally {
      runInAction(() => {
        this.isSending = false
      })
    }
  }
}
